import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { companyApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
  useToast,
} from '@/components/ui';
import { Fact, FactList } from '@/features/admin/Facts';
import { RecordHeader } from '@/features/admin/RecordHeader';
import { APPLICATION_LABELS, APPLICATION_TONES } from '@/features/applications/status';
import { ListingItems, ListingProse } from '@/features/internships/ListingText';
import { INTERNSHIP_STATUS_LABELS, INTERNSHIP_STATUS_TONES } from '@/features/internships/status';
import { VerifyToContinueButton } from '@/features/verification/CompanyVerificationGate';
import { useCompanyVerification } from '@/features/verification/useCompanyVerification';
import { errorMessage } from '@/hooks/useApiError';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';
import { deadlineLabel, formatDate, formatStipend, humanizeWorkMode } from '@/utils/format';

// docs/api.md: "Editing is allowed in DRAFT, REJECTED, PUBLISHED and CLOSED".
const EDITABLE = ['DRAFT', 'REJECTED', 'PUBLISHED', 'CLOSED'];
const PUBLISHABLE = ['DRAFT', 'REJECTED', 'CLOSED'];
const CLOSABLE = ['PUBLISHED', 'PENDING_APPROVAL'];

/** What the status means for the company, and what it can do next. */
const STATUS_NOTES = {
  DRAFT:
    'Only your team can see this draft. Submit it for review when it is ready; it goes live once Join-Wonder approves it.',
  PENDING_APPROVAL:
    'Waiting for Join-Wonder to approve it. It goes live as soon as it is approved; close it to withdraw it from review.',
  PUBLISHED: 'Live: students can find it and apply until the deadline.',
  REJECTED: 'Not approved. Fix what the reason says, then submit it for review again.',
  CLOSED:
    'Off discovery and not taking applications. You can edit it and submit it for review again.',
  ARCHIVED: 'Removed by Join-Wonder. It can no longer be edited or published.',
};

/**
 * One of the company's own listings, read-only: everything an applicant sees,
 * plus where it stands and who has applied. Editing is one click away rather
 * than the default, so opening a listing to check it cannot change it.
 */
export function CompanyInternshipDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = usePermissions();
  const { needsVerification } = useCompanyVerification();
  const canUpdate = can(PERMISSIONS.INTERNSHIP_UPDATE);
  const canPublish = can(PERMISSIONS.INTERNSHIP_PUBLISH);
  const canDelete = can(PERMISSIONS.INTERNSHIP_DELETE);
  const canSource = can(PERMISSIONS.CANDIDATE_READ);
  const canReadApplications = can(PERMISSIONS.APPLICATION_READ);
  const [confirming, setConfirming] = useState(null);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['company-internship', id],
    queryFn: () => companyApi.getInternship(id),
  });

  // The same key shape the applications page uses, so the two share a cache.
  const countParams = { internship_id: id };
  const { data: applicationCounts } = useQuery({
    queryKey: ['company-application-counts', countParams],
    queryFn: () => companyApi.getApplicationCounts(countParams),
    enabled: canReadApplications,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['company-internship', id] });
    void queryClient.invalidateQueries({ queryKey: ['company-internships'] });
    void queryClient.invalidateQueries({ queryKey: ['company-internship-counts'] });
  }

  const publish = useMutation({
    mutationFn: () => companyApi.publishInternship(id),
    onSuccess: (result) => {
      toast.success(result.message);
      invalidate();
    },
    onError: (publishError) =>
      toast.error(errorMessage(publishError, 'Could not publish the internship')),
  });

  const close = useMutation({
    mutationFn: () => companyApi.closeInternship(id),
    onSuccess: () => {
      toast.success('Internship closed');
      invalidate();
      setConfirming(null);
    },
    onError: (closeError) => {
      toast.error(errorMessage(closeError, 'Could not close the internship'));
      setConfirming(null);
    },
  });

  const remove = useMutation({
    mutationFn: () => companyApi.deleteInternship(id),
    onSuccess: () => {
      toast.success('Internship deleted');
      void queryClient.invalidateQueries({ queryKey: ['company-internships'] });
      void queryClient.invalidateQueries({ queryKey: ['company-internship-counts'] });
      queryClient.removeQueries({ queryKey: ['company-internship', id] });
      navigate(paths.company.internships, { replace: true });
    },
    onError: (deleteError) => {
      toast.error(errorMessage(deleteError, 'Could not delete the internship'));
      setConfirming(null);
    },
  });

  if (isPending) return <LoadingState label="Loading the internship…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const status = data.status;
  const skills = data.required_skills ?? [];
  const required = skills.filter((item) => item.is_required);
  const preferred = skills.filter((item) => !item.is_required);
  const pipeline = Object.keys(APPLICATION_LABELS)
    .map((key) => [key, applicationCounts?.[key] ?? 0])
    .filter(([, count]) => count > 0);
  const hasSections = data.description || data.responsibilities || data.requirements || data.perks;

  return (
    <div>
      <RecordHeader
        backTo={paths.company.internships}
        backLabel="Internships"
        eyebrow="Internship"
        title={data.title}
        leading={
          <span
            aria-hidden="true"
            className="bg-brand-50 text-brand-700 grid size-11 shrink-0 place-items-center rounded-xl"
          >
            <Icon name="internship" size={20} />
          </span>
        }
        subtitle={[
          data.location,
          data.work_mode ? humanizeWorkMode(data.work_mode) : null,
          data.job_category,
        ]
          .filter(Boolean)
          .join(' · ')}
        badges={
          <>
            <Badge dot tone={INTERNSHIP_STATUS_TONES[status] ?? 'neutral'}>
              {INTERNSHIP_STATUS_LABELS[status] ?? status}
            </Badge>
            {/* Live is not the same as open: a passed deadline stops
                applications without changing the status. */}
            {status === 'PUBLISHED' && (
              <Badge tone={data.is_open ? 'success' : 'neutral'}>
                {data.is_open ? 'Taking applications' : 'Deadline passed'}
              </Badge>
            )}
            {!data.is_paid && <Badge tone="neutral">Unpaid</Badge>}
          </>
        }
        actions={
          <>
            {canUpdate && EDITABLE.includes(status) && (
              <LinkButton to={paths.company.internshipEdit(id)} variant="secondary" size="sm">
                <Icon name="pencil" size={15} />
                Edit
              </LinkButton>
            )}
            {canPublish &&
              PUBLISHABLE.includes(status) &&
              (needsVerification ? (
                <VerifyToContinueButton size="sm" />
              ) : (
                <Button
                  variant="success"
                  size="sm"
                  isLoading={publish.isPending}
                  onClick={() => publish.mutate()}
                >
                  <Icon name="check" size={15} />
                  Submit for review
                </Button>
              ))}
            {canPublish && CLOSABLE.includes(status) && (
              <Button variant="ghostDanger" size="sm" onClick={() => setConfirming('close')}>
                <Icon name="archive" size={15} />
                Close
              </Button>
            )}
            {/* A live listing is closed first, so it never vanishes from
                under an applicant (the API answers 409 otherwise). */}
            {canDelete && status !== 'PUBLISHED' && (
              <Button variant="ghostDanger" size="sm" onClick={() => setConfirming('delete')}>
                <Icon name="trash" size={15} />
                Delete
              </Button>
            )}
          </>
        }
        facts={[
          {
            label: 'Applications',
            value: data.applications_count,
            hint: `${data.positions} ${data.positions === 1 ? 'position' : 'positions'} to fill`,
          },
          { label: 'Views', value: data.views_count },
          {
            label: 'Deadline',
            value: data.application_deadline ? formatDate(data.application_deadline) : 'None',
            hint: data.application_deadline ? deadlineLabel(data.application_deadline) : null,
          },
          {
            label: data.published_at ? 'Published' : 'Created',
            value: formatDate(data.published_at ?? data.created_at),
            hint: `Last edited ${formatDate(data.updated_at)}`,
          },
        ]}
      />

      {status === 'REJECTED' && data.rejection_reason && (
        <Alert tone="warning" title="Not approved" className="mb-6">
          {data.rejection_reason}
        </Alert>
      )}
      {status === 'CLOSED' && data.rejection_reason && (
        <Alert tone="warning" title="Taken down by Join-Wonder" className="mb-6">
          {data.rejection_reason}
        </Alert>
      )}
      {status === 'ARCHIVED' && data.rejection_reason && (
        <Alert tone="error" title="Removed by Join-Wonder" className="mb-6">
          {data.rejection_reason}
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader title="The listing" description="What applicants see." />
            <FactList columns={3}>
              <Fact
                label="Work mode"
                value={data.work_mode ? humanizeWorkMode(data.work_mode) : null}
              />
              <Fact label="Location" value={data.location} />
              <Fact
                label="Duration"
                value={
                  data.duration_months
                    ? `${data.duration_months} ${data.duration_months === 1 ? 'month' : 'months'}`
                    : null
                }
              />
              <Fact label="Starts" value={data.starts_on ? formatDate(data.starts_on) : null} />
              <Fact
                label="Stipend"
                value={
                  data.is_paid
                    ? formatStipend(data.stipend_min, data.stipend_max, data.stipend_currency)
                    : 'Unpaid'
                }
              />
              <Fact label="Positions" value={data.positions} />
              <Fact label="Category" value={data.job_category} />
              <Fact label="Industry" value={data.industry} />
            </FactList>

            {hasSections ? (
              <div className="border-line mt-6 space-y-6 border-t pt-6">
                <Section title="About the internship">
                  <ListingProse text={data.description} />
                </Section>
                <Section title="Responsibilities" show={data.responsibilities}>
                  <ListingItems text={data.responsibilities} />
                </Section>
                <Section title="Requirements" show={data.requirements}>
                  <ListingItems text={data.requirements} />
                </Section>
                <Section title="Perks" show={data.perks}>
                  <ListingItems text={data.perks} />
                </Section>
              </div>
            ) : (
              <p className="text-ink-muted mt-6 text-sm">No description yet.</p>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Skills"
              description="What candidates are matched and ranked against."
            />
            {skills.length === 0 ? (
              <EmptyState
                compact
                icon={<Icon name="sparkles" size={20} />}
                title="No skills listed"
                description="Without skills there is little to match candidates on."
              />
            ) : (
              <div className="space-y-4">
                <SkillGroup label="Required" items={required} tone="brand" />
                <SkillGroup label="Nice to have" items={preferred} tone="neutral" />
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Status" />
            <Badge dot tone={INTERNSHIP_STATUS_TONES[status] ?? 'neutral'}>
              {INTERNSHIP_STATUS_LABELS[status] ?? status}
            </Badge>
            <p className="text-ink-muted mt-2 text-sm">
              {status === 'PUBLISHED' && !data.is_open
                ? 'Live, but the deadline has passed, so it no longer takes applications. Edit the deadline to reopen it, or close it.'
                : STATUS_NOTES[status]}
            </p>
            {status === 'PUBLISHED' && (
              <Link
                to={paths.internshipDetail(data.slug)}
                className="text-brand-700 mt-3 inline-flex items-center gap-1 text-sm hover:underline"
              >
                View the public page
                <Icon name="external" size={14} />
              </Link>
            )}
          </Card>

          {canReadApplications && (
            <Card>
              <CardHeader title="Applicants" description={`${data.applications_count} in total`} />
              {pipeline.length === 0 ? (
                <EmptyState compact title="Nobody has applied yet" />
              ) : (
                <dl className="space-y-2 text-sm">
                  {pipeline.map(([key, count]) => (
                    <div key={key} className="flex items-center justify-between gap-3">
                      <dt>
                        <Badge dot tone={APPLICATION_TONES[key] ?? 'neutral'}>
                          {APPLICATION_LABELS[key]}
                        </Badge>
                      </dt>
                      <dd className="text-ink font-medium tabular-nums">{count}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {data.applications_count > 0 && (
                <LinkButton
                  to={`${paths.company.applications}?internship=${id}`}
                  variant="secondary"
                  size="sm"
                  className="mt-4 w-full"
                >
                  Review applicants
                </LinkButton>
              )}
            </Card>
          )}

          {(data.published_at || (canSource && status === 'PUBLISHED')) && (
            <Card>
              <CardHeader title="More" />
              <div className="flex flex-col gap-2">
                {/* A listing that never went live has no audience to report on. */}
                {data.published_at && (
                  <LinkButton
                    to={paths.company.internshipPerformance(id)}
                    variant="secondary"
                    size="sm"
                  >
                    <Icon name="chartBar" size={15} />
                    Performance
                  </LinkButton>
                )}
                {canSource && status === 'PUBLISHED' && (
                  <LinkButton
                    to={paths.company.internshipSourcing(id)}
                    variant="secondary"
                    size="sm"
                  >
                    <Icon name="users" size={15} />
                    Find candidates
                  </LinkButton>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirming === 'close'}
        title="Close internship"
        message={`"${data.title}" will stop being shown to students and stop taking applications. Applications already made are kept, and you can publish it again later.`}
        confirmLabel="Close"
        isLoading={close.isPending}
        onConfirm={() => close.mutate()}
        onCancel={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={confirming === 'delete'}
        title="Delete internship"
        message={`"${data.title}" will be removed. This cannot be undone.`}
        confirmLabel="Delete"
        isLoading={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirming(null)}
      />
    </div>
  );
}

function Section({ title, show = true, children }) {
  if (!show) return null;
  return (
    <section>
      <h3 className="text-ink mb-2 text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function SkillGroup({ label, items, tone }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="text-ink-muted text-xs">{label}</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {items.map((item) => (
          <Badge key={item.skill.id} tone={tone}>
            {item.skill.name}
          </Badge>
        ))}
      </div>
    </div>
  );
}
